// =============================================================================
// OELOX — Shared PWA Bootstrap
// =============================================================================

import { db, migrateFromLocalStorage, loadCart, saveCart, clearCart, getCachedMenuItems, cacheMenuItems, cacheTenant, getCachedTenant, getCachedCategories, cacheCategories, getCachedOrders, cacheOrders, getCachedDrivers, cacheDrivers, getCachedMenuItemsSync, getCachedCategoriesSync, getCartSync, getCachedOrdersSync, primeMenuCacheFromIndexedDB, type QueuedAction, type CartLine, type CachedMenuItem, type CachedDriver } from "./db";
// Install prompts, service worker registration, offline/update banners.
// Imported by every PWA app in the monorepo.
// =============================================================================

// ── Install Prompt ──────────────────────────────────────────────────────────

let deferredPrompt: Event | null = null;

type InstallCallback = (prompt: Event) => void;
let onInstallAvailable: InstallCallback | null = null;

export function captureInstallPrompt(callback?: InstallCallback): void {
  if (callback) {
    onInstallAvailable = callback;
  }

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    onInstallAvailable?.(e);
  });
}

export async function triggerInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await (deferredPrompt as any).prompt();
    deferredPrompt = null;
    return result.outcome === "accepted";
  } catch {
    return false;
  }
}

export function getiOSInstallInstructions(): string {
  const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
  if (!isIOS) return "";
  return "Tap the Share button and then 'Add to Home Screen' to install this app.";
}

// ── Service Worker ──────────────────────────────────────────────────────────

type SWUpdateCallback = (registration: ServiceWorkerRegistration) => void;
let onUpdateAvailable: SWUpdateCallback | null = null;
const onUpdateApplied: (() => void) | null = null;

export function registerSW(
  swPath = "/sw.js",
  callbacks?: {
    onUpdate?: SWUpdateCallback;
    onSuccess?: (reg: ServiceWorkerRegistration) => void;
  }
): void {
  if (!("serviceWorker" in navigator)) return;

  onUpdateAvailable = callbacks?.onUpdate ?? null;

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register(swPath);

      registration.addEventListener("updatefound", () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.addEventListener("statechange", () => {
          if (
            installingWorker.state === "installed" &&
            navigator.serviceWorker.controller
          ) {
            onUpdateAvailable?.(registration);
          }
        });
      });

      callbacks?.onSuccess?.(registration);
    } catch (err) {
      console.error("[PWA] Service worker registration failed:", err);
    }
  });
}

export function applyUpdate(registration: ServiceWorkerRegistration): void {
  const waitingWorker = registration.waiting;
  if (!waitingWorker) return;

  waitingWorker.addEventListener("statechange", () => {
    if (waitingWorker.state === "activated") {
      onUpdateApplied?.();
      window.location.reload();
    }
  });

  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

// ── Offline Queue (IndexedDB via Dexie) ─────────────────────────────────────

export type { QueuedAction } from "./db";

export { loadCart, saveCart, clearCart, getCachedMenuItems, cacheMenuItems, cacheTenant, getCachedTenant, getCachedCategories, cacheCategories, getCachedOrders, cacheOrders, getCachedDrivers, cacheDrivers, getCachedMenuItemsSync, getCachedCategoriesSync, getCartSync, getCachedOrdersSync, primeMenuCacheFromIndexedDB };
export type { CartLine, CachedMenuItem, CachedDriver };

const MAX_RETRIES = 5;

// One-time migration from old localStorage queue on first load
migrateFromLocalStorage();

export function queueAction(type: string, payload: unknown): void {
  db.queue.put({
    id: crypto.randomUUID(),
    type,
    payload,
    timestamp: Date.now(),
    retryCount: 0,
  }).catch(() => { /* IndexedDB unavailable — silent */ });
}

type ActionHandler = (action: QueuedAction) => Promise<void>;
const actionHandlers: Map<string, ActionHandler> = new Map();

export function registerActionHandler(type: string, handler: ActionHandler): void {
  actionHandlers.set(type, handler);
}

export async function getDeadLetterQueue(): Promise<QueuedAction[]> {
  try {
    return await db.dead_letter.orderBy("timestamp").toArray();
  } catch {
    return [];
  }
}

export async function flushQueue(): Promise<{ success: number; failed: number; dead: number }> {
  let queue: QueuedAction[];
  try {
    queue = await db.queue.orderBy("timestamp").toArray();
  } catch {
    return { success: 0, failed: 0, dead: 0 };
  }
  if (queue.length === 0) return { success: 0, failed: 0, dead: 0 };

  let success = 0;
  let failed = 0;
  let dead = 0;

  for (const action of queue) {
    const handler = actionHandlers.get(action.type);
    if (!handler) continue; // kept in IndexedDB, will retry if handler registered later
    try {
      await handler(action);
      await db.queue.delete(action.id);
      success++;
    } catch {
      action.retryCount = (action.retryCount ?? 0) + 1;
      if (action.retryCount >= MAX_RETRIES) {
        try {
          await db.dead_letter.put(action);
          await db.queue.delete(action.id);
        } catch { /* silent */ }
        dead++;
      } else {
        try { await db.queue.put(action); } catch { /* silent */ }
        failed++;
      }
    }
  }

  return { success, failed, dead };
}

export function registerAutoFlush(): void {
  window.addEventListener("online", () => {
    flushQueue();
  });

  // Also try on load
  if (navigator.onLine) {
    flushQueue();
  }
}

export async function getQueueSize(): Promise<number> {
  try {
    return await db.queue.count();
  } catch {
    return 0;
  }
}

// ── Supabase URL helper (used by useBackendReachable) ───────────────────────

function getSupabaseBaseUrl(): string {
  if (typeof window === "undefined") return "";
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const url = env?.VITE_SUPABASE_URL;
  if (url) return url;
  const runtimeConfig = (window as Window & {
    OELOX_PUBLIC_CONFIG?: { VITE_SUPABASE_URL?: string; supabaseUrl?: string };
  }).OELOX_PUBLIC_CONFIG;
  return runtimeConfig?.VITE_SUPABASE_URL ?? runtimeConfig?.supabaseUrl ?? "";
}

// ── React Components (renderless hook interface) ─────────────────────────────

import { useState, useEffect, useCallback } from "react";

export function useInstallPrompt() {
  const [isInstallable, setIsInstallable] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    captureInstallPrompt(() => setIsInstallable(true));
    const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    if (isIOS && !window.matchMedia("(display-mode: standalone)").matches) {
      setShowIOSInstructions(true);
    }
  }, []);

  const install = useCallback(async () => {
    return triggerInstall();
  }, []);

  return { isInstallable, showIOSInstructions, install };
}

export function useUpdatePrompt() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingReg, setWaitingReg] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    onUpdateAvailable = (reg) => {
      setUpdateAvailable(true);
      setWaitingReg(reg);
    };
  }, []);

  const apply = useCallback(() => {
    if (waitingReg) {
      applyUpdate(waitingReg);
    }
  }, [waitingReg]);

  return { updateAvailable, apply };
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return isOnline;
}

// ── Backend Reachability (Lie-Fi Detection) ─────────────────────────────────
// Lightweight HEAD ping to Supabase base endpoint. No auth required.
// Cached for 30 seconds. Used instead of navigator.onLine for all offline decisions.

const REACHABILITY_KEY = "oelox_backend_reachable";
const REACHABILITY_TTL = 30_000;

export function useBackendReachable(): boolean {
  const isOnline = useNetworkStatus();
  const [reachable, setReachable] = useState<boolean>(() => {
    // Only trust sessionStorage cache if it matches the current isOnline state.
    // A stale cache from a previous online/offline session causes swapped logic
    // (online→shows offline toast, offline→shows error toast).
    try {
      const cached = sessionStorage.getItem(REACHABILITY_KEY);
      if (cached) {
        const { value, ts } = JSON.parse(cached);
        if (Date.now() - ts < REACHABILITY_TTL && (value as boolean) === isOnline) {
          return value as boolean;
        }
      }
    } catch { /* ignore */ }
    return isOnline;
  });

  useEffect(() => {
    if (!isOnline) { setReachable(false); return; }
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout>;

    const ping = async () => {
      const baseUrl = getSupabaseBaseUrl();
      if (!baseUrl) { setReachable(false); return; }
      try {
        const controller = new AbortController();
        timeout = setTimeout(() => controller.abort(), 5000);
        await fetch(baseUrl + "/rest/v1/", { method: "HEAD", signal: controller.signal });
        clearTimeout(timeout);
        if (!cancelled) {
          setReachable(true);
          sessionStorage.setItem(REACHABILITY_KEY, JSON.stringify({ value: true, ts: Date.now() }));
        }
      } catch {
        clearTimeout(timeout);
        if (!cancelled) {
          setReachable(false);
          sessionStorage.setItem(REACHABILITY_KEY, JSON.stringify({ value: false, ts: Date.now() }));
        }
      }
    };

    ping();
    const interval = setInterval(ping, REACHABILITY_TTL);
    return () => { cancelled = true; clearInterval(interval); clearTimeout(timeout); };
  }, [isOnline]);

  return reachable;
}
